import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import Highlighter from 'react-highlight-words';
import {
    formatMetricName,
    getRoleDisplayName,
    getTranslatedItemType,
    MANAGE_PERMISSIONS_STEPS,
    MEMBER_TYPES,
    SECTIONS_STEPS,
    translationPrefix,
} from '../../utils';
import AppWithSitesItem from './AppWithSitesItem';
import CollapsibleItemGrid from './CollapsibleItemGrid';
import GroupedContent from './GroupedContent';
import ReviewCard from './ReviewCard';
import ScreenItem from './ScreenItem';
import SectionsReviewContent from './SectionsReviewContent';
import useReviewData, { getDimensionData } from './useReviewData';
import UserDetailField from './UserDetailField';
import css from './ReviewPageStep.css';

const FUTURE_ITEMS_TRANSLATION_KEYS = {
    [MEMBER_TYPES.AGENCY]: {
        appsites: 'APPSITES_AGENCIES',
    },
};

const getFutureItemsTranslationKey = (activeMemberType, itemType) => {
    const key = FUTURE_ITEMS_TRANSLATION_KEYS[activeMemberType]?.[itemType] || 'GENERAL';
    return `${translationPrefix}.FUTURE_ITEMS_WILL_BE_ADDED.${key}`;
};

const EmptyState = ({ itemType }) => (
    <div className={css.emptyState}>
        <Translate id={`${translationPrefix}.REVIEW.EMPTY_STATE`} data={{ itemType }} />
    </div>
);

EmptyState.propTypes = {
    itemType: PropTypes.string.isRequired,
};

const ReviewPageStep = ({
    translate,
    registrationForm,
    permissionDimensions,
    screens,
    fields,
    roleSelectionForm,
    pages = [],
    activeMemberType,
    sectionPermissions = [],
    disabledSourceNames = [],
    sdkEventsData,
    onEditStep = () => {},
    onStepValidationStatusChanged = () => {},
}) => {
    const { appsData, sourcesData, screensData, fieldsData, isAppLevel } = useReviewData({
        permissionDimensions,
        disabledSourceNames,
        screens,
        fields,
    });

    useEffect(() => {
        onStepValidationStatusChanged({ isValid: true, title: '' });
    }, [onStepValidationStatusChanged]);

    const futureDesc = itemType => getFutureItemsTranslationKey(activeMemberType, itemType);

    const renderRoleSection = pageId => (
        <ReviewCard pageId={pageId} onEditStep={onEditStep} title={`${translationPrefix}.REVIEW.USER_ROLE`}>
            <div className={css.userTypeText}>{getRoleDisplayName(roleSelectionForm.role, translate)}</div>
        </ReviewCard>
    );

    const renderUserDetailsSection = pageId => {
        const { firstName, lastName, email, description } = registrationForm;
        const fullName =
            firstName.value || lastName.value
                ? `${firstName.value} ${lastName.value}`
                : translate(`${translationPrefix}.REVIEW.NA`);
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.USER_DETAILS_TITLE`}
            >
                <div className={css.userDetailsArea}>
                    <UserDetailField labelId={`${translationPrefix}.REVIEW.USER_NAME`}>{fullName}</UserDetailField>
                    <UserDetailField labelId={`${translationPrefix}.REVIEW.EMAIL`}>{email.value}</UserDetailField>
                    <UserDetailField labelId={`${translationPrefix}.REVIEW.DESCRIPTION`}>
                        {description?.value ? (
                            description.value
                        ) : (
                            <Translate id={`${translationPrefix}.REVIEW.NO_DESCRIPTION`} />
                        )}
                    </UserDetailField>
                </div>
            </ReviewCard>
        );
    };

    const renderAppsSection = pageId => {
        if (!appsData) return null;
        const itemType = isAppLevel ? 'apps' : 'appsites';
        const allAppsSelected = !isAppLevel && appsData.items.length === appsData.totalCount && appsData.totalCount > 0;
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.APPS_TITLE`}
                titleTextReplace={{ count: appsData.items.length, overall: appsData.totalCount }}
                info={
                    !isAppLevel
                        ? translate('STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.ITEM_SELECTED', {
                              selected: appsData.selectedSitesCount,
                              total: appsData.totalSitesCount,
                              itemType: getTranslatedItemType(translate, 'appsites'),
                          })
                        : ''
                }
                highlightInfo={allAppsSelected && appsData.selectedSitesCount < appsData.totalSitesCount}
                description={appsData.addFuture ? futureDesc(itemType) : ''}
                descriptionTextReplace={{ itemType: getTranslatedItemType(translate, 'apps') }}
            >
                <CollapsibleItemGrid items={appsData.items} renderItem={!isAppLevel ? AppWithSitesItem : undefined} />
            </ReviewCard>
        );
    };

    const renderSourcesSection = pageId => {
        if (!sourcesData) return null;
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.SOURCES_TITLE`}
                titleTextReplace={{ count: sourcesData.items.length, overall: sourcesData.totalCount }}
                description={sourcesData.addFuture ? futureDesc('sources') : ''}
                descriptionTextReplace={{ itemType: getTranslatedItemType(translate, 'sources') }}
            >
                <CollapsibleItemGrid items={sourcesData.items} />
            </ReviewCard>
        );
    };

    const renderScreensSection = pageId => {
        if (!screensData) return null;
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.SCREENS_TITLE`}
                titleTextReplace={{ count: screensData.selectedCount, overall: screensData.overall }}
                description={screensData.addFuture ? futureDesc('screens') : ''}
                descriptionTextReplace={{ itemType: getTranslatedItemType(translate, 'screens') }}
            >
                {screensData.selectedCount > 0 ? (
                    <GroupedContent
                        data={screensData.values.map(section => ({
                            key: section.name,
                            title: section.displayName,
                            items: section.children.map(screen => ({
                                key: screen.name,
                                searchText: screen.displayName || '',
                                ...screen,
                            })),
                        }))}
                        columns={4}
                        renderItem={(item, search) => (
                            <ScreenItem screen={item} sectionPermissions={sectionPermissions} search={search} />
                        )}
                    />
                ) : (
                    <EmptyState itemType={getTranslatedItemType(translate, 'screens')} />
                )}
            </ReviewCard>
        );
    };

    const renderMetricsSection = pageId => {
        if (!fieldsData) return null;
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.METRICS_TITLE`}
                titleTextReplace={{ count: fieldsData.selectedCount, overall: fieldsData.overall }}
                description={fieldsData.addFuture ? futureDesc('metrics') : ''}
                descriptionTextReplace={{ itemType: getTranslatedItemType(translate, 'metrics') }}
            >
                {fieldsData.selectedCount > 0 ? (
                    <GroupedContent
                        data={fieldsData.values.map(fieldType => ({
                            key: fieldType.type,
                            title: translate(fieldType.label),
                            items: fieldType.values.map(field => ({
                                key: field.id,
                                searchText: formatMetricName(field) || '',
                            })),
                        }))}
                        columns={5}
                    />
                ) : (
                    <EmptyState itemType={getTranslatedItemType(translate, 'metrics')} />
                )}
            </ReviewCard>
        );
    };

    const renderSectionsSection = pageId => (
        <ReviewCard
            pageId={pageId}
            onEditStep={onEditStep}
            title={`${translationPrefix}.REVIEW.SECTIONS_TITLE.${pageId.toUpperCase()}`}
        >
            <SectionsReviewContent stepName={pageId} sectionPermissions={sectionPermissions} />
        </ReviewCard>
    );

    const renderDimensionSection = pageId => {
        const dimension = permissionDimensions[pageId];
        if (!dimension) return null;
        const dimData = getDimensionData(dimension);
        return (
            <ReviewCard
                pageId={pageId}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.DIMENSION_FILTER_TITLE`}
                titleTextReplace={{
                    dimension: dimData.displayName,
                    count: dimData.selectedCount,
                    overall: dimData.totalCount,
                }}
                description={dimData.addFuture ? futureDesc('values') : ''}
                descriptionTextReplace={{
                    itemType: getTranslatedItemType(translate, 'dimension_values', {
                        dimension: dimension.displayName.toLowerCase(),
                    }),
                }}
            >
                <CollapsibleItemGrid
                    items={dimData.items}
                    renderItem={({ item, search }) => (
                        <div className={css.dimensionValue}>
                            <Highlighter searchWords={[search]} textToHighlight={item.displayName} autoEscape />
                        </div>
                    )}
                />
            </ReviewCard>
        );
    };

    const renderSdkEventsSection = () => {
        if (!sdkEventsData) return null;
        return (
            <ReviewCard
                pageId={MANAGE_PERMISSIONS_STEPS.attribution}
                onEditStep={onEditStep}
                title={`${translationPrefix}.REVIEW.SDK_EVENTS_TITLE`}
                titleTextReplace={{ count: sdkEventsData.selectedCount, overall: sdkEventsData.overall }}
                description={sdkEventsData.addFuture ? futureDesc('sdk_event') : ''}
                descriptionTextReplace={{ itemType: getTranslatedItemType(translate, 'sdk_event') }}
            >
                {sdkEventsData.selectedCount > 0 ? (
                    <CollapsibleItemGrid
                        items={sdkEventsData.items}
                        renderItem={({ item, search }) => (
                            <div className={css.sdkEvent}>
                                <Highlighter searchWords={[search]} textToHighlight={item.displayName} autoEscape />
                            </div>
                        )}
                    />
                ) : (
                    <EmptyState itemType={getTranslatedItemType(translate, 'sdk_event')} />
                )}
            </ReviewCard>
        );
    };

    const SECTION_RENDERERS = {
        [MANAGE_PERMISSIONS_STEPS.roleSelection]: renderRoleSection,
        [MANAGE_PERMISSIONS_STEPS.userDetails]: renderUserDetailsSection,
        [MANAGE_PERMISSIONS_STEPS.apps]: renderAppsSection,
        [MANAGE_PERMISSIONS_STEPS.sources]: renderSourcesSection,
        [MANAGE_PERMISSIONS_STEPS.screens]: renderScreensSection,
        [MANAGE_PERMISSIONS_STEPS.metrics]: renderMetricsSection,
    };

    const renderSection = pageId => {
        const renderer = SECTION_RENDERERS[pageId];
        if (renderer) return renderer(pageId);
        if (SECTIONS_STEPS.includes(pageId)) return renderSectionsSection(pageId);
        return renderDimensionSection(pageId);
    };

    return (
        <div className={css.container}>
            {pages.map(pageId => {
                const section = renderSection(pageId);
                return section ? (
                    <React.Fragment key={pageId}>
                        {section}
                        {pageId === MANAGE_PERMISSIONS_STEPS.attribution && renderSdkEventsSection()}
                    </React.Fragment>
                ) : null;
            })}
        </div>
    );
};

ReviewPageStep.propTypes = {
    registrationForm: PropTypes.shape({
        firstName: PropTypes.shape({ value: PropTypes.string }),
        lastName: PropTypes.shape({ value: PropTypes.string }),
        email: PropTypes.shape({ value: PropTypes.string }),
        description: PropTypes.shape({ value: PropTypes.string }),
    }),
    permissionDimensions: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string,
            displayName: PropTypes.string,
            values: PropTypes.arrayOf(
                PropTypes.shape({ name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })
            ),
            selected: PropTypes.shape({
                values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
                addFuture: PropTypes.bool,
            }),
        })
    ),
    screens: PropTypes.shape({
        values: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
        addFuture: PropTypes.bool,
        overall: PropTypes.number,
    }),
    fields: PropTypes.shape({
        values: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string })),
        addFuture: PropTypes.bool,
        overall: PropTypes.number,
    }),
    roleSelectionForm: PropTypes.shape({
        role: PropTypes.number,
    }),
    pages: PropTypes.arrayOf(PropTypes.string),
    activeMemberType: PropTypes.string,
    sectionPermissions: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
    disabledSourceNames: PropTypes.arrayOf(PropTypes.string),
    sdkEventsData: PropTypes.shape({
        items: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, displayName: PropTypes.string })),
        selectedCount: PropTypes.number,
        overall: PropTypes.number,
        addFuture: PropTypes.bool,
    }),
    onEditStep: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(ReviewPageStep);
